import type { Asset } from "rujira.js";

export type PrivateAssetFlow =
  | "deposit"
  | "reserve-to-spending"
  | "spending-to-reserve"
  | "withdraw";

/**
 * Keep the visible Private Reserve/Spending controls independent from the
 * contract implementation. Both privacy implementations use this exact flow
 * map; only the message translation behind it changes.
 */
export const privateAssetFlow = (
  isDeposit: boolean,
  isSubWallet = false
): PrivateAssetFlow => {
  if (isDeposit) return isSubWallet ? "reserve-to-spending" : "deposit";
  return isSubWallet ? "spending-to-reserve" : "withdraw";
};

export type PrivateAssetSource = "public" | "reserve" | "spending";

/**
 * The privacy proxy lives on THORChain. A direct Reserve deposit therefore
 * cannot be signed by an EVM/BTC account even when GraphQL groups that account
 * under the same secured-asset balance.
 */
export const privateDepositSourceAccounts = <T extends { address: string }>(
  accounts: readonly T[]
): T[] =>
  accounts.filter(
    (account) =>
      account.address.startsWith("thor1") ||
      account.address.startsWith("tthor1")
  );

export const privateAssetSource = (
  flow: PrivateAssetFlow
): PrivateAssetSource => {
  switch (flow) {
    case "deposit":
      return "public";
    case "reserve-to-spending":
    case "withdraw":
      return "reserve";
    case "spending-to-reserve":
      return "spending";
  }
};

/** Reserve entries represent recovered notes, not wallet signer addresses. */
export const privateFlowSourceAccounts = <
  T extends { address: string; chain?: string },
>(
  flow: PrivateAssetFlow,
  accounts: readonly T[]
): T[] => {
  const source = privateAssetSource(flow);
  if (source === "reserve") {
    return accounts.filter((account) => account.chain === "STEALTH");
  }
  const walletAccounts = privateDepositSourceAccounts(accounts);
  return source === "spending"
    ? walletAccounts.filter((account) => account.chain === "THOR")
    : walletAccounts;
};

export const privateFlowLimit = (
  flow: PrivateAssetFlow,
  denom: string,
  reserveBalances: ReadonlyMap<string, bigint>,
  spendingBalances: readonly {
    asset: Asset;
    accounts: readonly { chain: string; balance: bigint }[];
  }[]
): bigint | undefined => {
  if (flow === "reserve-to-spending" || flow === "withdraw") {
    return reserveBalances.get(denom) ?? 0n;
  }
  if (flow === "spending-to-reserve") {
    return (
      spendingBalances
        .find((balance) => balance.asset.variants?.native?.denom === denom)
        ?.accounts.filter((account) => account.chain === "THOR")
        .reduce((total, account) => total + account.balance, 0n) ?? 0n
    );
  }
  return undefined;
};
